import { useState, useEffect } from 'react';
import { Moon, Sun, Menu, LogIn, UserPlus, Key, LogOut, User, Ticket, Settings, MessageCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { NavigationMenu, NavigationMenuList, NavigationMenuItem, NavigationMenuLink } from '@/components/ui/navigation-menu';
import { cn } from '@/lib/utils';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useAuth } from '@/contexts/AuthContext';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { useNavigate, useLocation } from 'react-router-dom';
import LanguageSelector from '@/components/LanguageSelector';
import { useLanguage } from '@/contexts/LanguageContext';
import { useSupportChat } from '@/hooks/useSupportChat';

interface HeaderProps {
  onMobileMenuToggle: () => void;
}

export const Header = ({ onMobileMenuToggle }: HeaderProps) => {
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [dialogOpen, setDialogOpen] = useState<'login' | 'register' | 'recovery' | null>(null);
  const [loginForm, setLoginForm] = useState({ usernameOrEmail: '', password: '' });
  const [registerForm, setRegisterForm] = useState({ name: '', email: '', password: '' });
  const [recoveryEmail, setRecoveryEmail] = useState('');
  const { user, isAuthenticated, login, register, logout, recoverPassword } = useAuth();
  const [selectedMatchesCount, setSelectedMatchesCount] = useState(0);
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useLanguage();
  const { hasUnreadMessages } = useSupportChat();
  
  useEffect(() => {
    const loadSelectedMatchesCount = () => {
      try {
        const storedMatches = localStorage.getItem('selectedMatches');
        if (storedMatches) {
          const parsedMatches = JSON.parse(storedMatches);
          setSelectedMatchesCount(parsedMatches.length);
        }
      } catch (error) {
        console.error("Error loading selected matches count:", error);
      }
    };
    
    loadSelectedMatchesCount();
    
    const handleMatchesUpdated = (event: Event) => {
      const customEvent = event as CustomEvent;
      const updatedMatches = customEvent.detail;
      setSelectedMatchesCount(updatedMatches.length);
    };
    
    window.addEventListener('selectedMatchesUpdated', handleMatchesUpdated);
    
    return () => {
      window.removeEventListener('selectedMatchesUpdated', handleMatchesUpdated);
    };
  }, []);
  
  const toggleDarkMode = () => {
    setIsDarkMode(!isDarkMode);
    
    if (isDarkMode) {
      document.documentElement.classList.remove('dark');
    } else {
      document.documentElement.classList.add('dark');
    }
  };

  const handleLoginSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await login(loginForm.usernameOrEmail, loginForm.password);
      setDialogOpen(null);
      setLoginForm({ usernameOrEmail: '', password: '' });
    } catch (error) {
    }
  };

  const handleRegisterSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await register({
        username: registerForm.name,
        email: registerForm.email,
        password: registerForm.password,
        preferred_username: registerForm.name,
        gender: 'other'
      });
      setDialogOpen(null);
      setRegisterForm({ name: '', email: '', password: '' });
    } catch (error) {
    }
  };

  const handleRecoverySubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await recoverPassword(recoveryEmail);
      setDialogOpen(null);
      setRecoveryEmail('');
    } catch (error) {
    }
  };
  
  const getUserInitials = (name: string) => {
    return name
      .split(' ')
      .map(part => part.charAt(0))
      .join('')
      .toUpperCase()
      .substring(0, 2);
  };

  const getUserTypeBadgeColor = (userType: string) => {
    switch (userType) {
      case 'Admin':
        return 'bg-red-500';
      case 'VIP':
        return 'bg-yellow-500';
      default:
        return 'bg-blue-500';
    }
  };

  const navigateToTickets = () => {
    navigate('/tickets');
    
    setTimeout(() => {
      const event = new CustomEvent('selectTicketsTab', { detail: { tab: 'tickets' } });
      window.dispatchEvent(event);
    }, 50);
  };
  
  const navigateToSettings = () => {
    navigate('/settings');
  };

  const navigateToSupport = () => {
    navigate('/support');
  };

  return (
    <header className="titan-glass sticky top-0 z-30 flex h-16 items-center justify-between border-b border-border/40 px-4 backdrop-blur-sm">
      <button
        onClick={onMobileMenuToggle}
        className="p-2 rounded-full hover:bg-muted md:hidden"
        aria-label="Toggle mobile menu"
      >
        <Menu size={24} />
      </button>
      
      <div className="flex items-center justify-end space-x-2 sm:space-x-4 ml-auto">
        <div className="flex items-center space-x-2 sm:space-x-3">
          {selectedMatchesCount > 0 && (
            <div className="relative flex items-center">
              <Button variant="ghost" size="icon" className="relative" onClick={navigateToTickets}>
                <Ticket size={18} />
                <span className="absolute -top-2 -right-2 flex items-center justify-center min-w-[18px] h-[18px] text-[10px] font-medium bg-primary text-primary-foreground rounded-full">
                  {selectedMatchesCount}
                </span>
              </Button>
            </div>
          )}
          
          <LanguageSelector />
          
          <button
            onClick={toggleDarkMode}
            className="relative flex h-7 w-10 items-center rounded-full bg-muted p-1 transition-colors"
            aria-label="Toggle dark mode"
          >
            <div className={`
              absolute ${isDarkMode ? 'translate-x-3' : 'translate-x-0'}
              h-5 w-5 rounded-full bg-white shadow transition-transform
            `}>
              <span className={`
                absolute inset-0 flex items-center justify-center text-xs
                ${isDarkMode ? 'text-black' : 'text-orange-500'}
              `}>
                {isDarkMode ? <Moon size={12} /> : <Sun size={12} />}
              </span>
            </div>
          </button>
          
          {isAuthenticated && user ? (
            <div className="flex items-center gap-1 sm:gap-2">
              <div className="hidden md:flex flex-col items-end">
                <span className="text-sm font-medium truncate max-w-[100px] lg:max-w-[150px]">{user.name}</span>
                <span className={`text-xs px-2 py-0.5 rounded-full ${getUserTypeBadgeColor(user.userType)} text-white`}>
                  {user.userType}
                </span>
              </div>
              
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="h-8 w-8 rounded-full p-0">
                    <Avatar>
                      <AvatarImage src={user.avatar} alt={user.name} />
                      <AvatarFallback>{getUserInitials(user.name)}</AvatarFallback>
                    </Avatar>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem className="flex items-center gap-2" onClick={navigateToSettings}>
                    <Settings className="h-4 w-4" />
                    <span className="whitespace-nowrap">{t('settings.accountSettings')}</span>
                  </DropdownMenuItem>
                  <DropdownMenuItem className="flex items-center gap-2" onClick={navigateToSupport}>
                    <div className="relative">
                      <MessageCircle className="h-4 w-4" />
                      {hasUnreadMessages && (
                        <div className="absolute -top-1 -right-1 w-2 h-2 bg-red-500 rounded-full"></div>
                      )}
                    </div>
                    <span className="whitespace-nowrap">Contact Support</span>
                  </DropdownMenuItem>
                  <DropdownMenuItem className="flex items-center gap-2" onClick={logout}>
                    <LogOut className="h-4 w-4" />
                    <span className="whitespace-nowrap">{t('auth.logout')}</span>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          ) : (
            <div className="flex items-center space-x-1 sm:space-x-2">
              <Button 
                variant="outline" 
                size="sm" 
                className="h-7 px-2 text-xs flex items-center space-x-1 hover:bg-accent hover:text-accent-foreground transition-all duration-300"
                onClick={() => setDialogOpen('login')}
              >
                <LogIn className="h-3 w-3" />
                <span className="whitespace-nowrap">{t('auth.login')}</span>
              </Button>
              
              <Button 
                variant="default" 
                size="sm" 
                className="h-7 px-2 text-xs flex items-center space-x-1 transition-all duration-300"
                onClick={() => setDialogOpen('register')}
              >
                <UserPlus className="h-3 w-3" />
                <span className="whitespace-nowrap">{t('auth.register')}</span>
              </Button>
            </div>
          )}
        </div>
      </div>

      <Dialog open={dialogOpen === 'login'} onOpenChange={(open) => !open && setDialogOpen(null)}>
        <DialogContent className="sm:max-w-md animate-fade-in">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold">{t('auth.login')}</DialogTitle>
            <DialogDescription>
              {t('auth.loginDesc')}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleLoginSubmit} className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="usernameOrEmail">{t('form.username')}</Label>
              <Input
                id="usernameOrEmail"
                type="text"
                placeholder={t('form.usernamePlaceholder')}
                value={loginForm.usernameOrEmail}
                onChange={(e) => setLoginForm({ ...loginForm, usernameOrEmail: e.target.value })}
                required
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="password">{t('form.password')}</Label>
              <Input
                id="password"
                type="password"
                placeholder="••••••••"
                value={loginForm.password}
                onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })}
                showPasswordToggle={true}
                required
              />
            </div>
            <Button type="submit" className="w-full">{t('auth.login')}</Button>
            <div className="flex justify-between items-center mt-2">
              <Button variant="ghost" size="sm" className="text-xs text-muted-foreground hover:text-foreground" onClick={() => setDialogOpen('recovery')} type="button">
                {t('form.forgotPassword')}
              </Button>
              <Button variant="ghost" size="sm" className="text-xs text-muted-foreground hover:text-foreground" onClick={() => setDialogOpen('register')} type="button">
                {t('form.createAccount')}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={dialogOpen === 'register'} onOpenChange={(open) => !open && setDialogOpen(null)}>
        <DialogContent className="sm:max-w-md animate-fade-in">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold">{t('auth.register')}</DialogTitle>
            <DialogDescription>
              {t('auth.registerDesc')}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleRegisterSubmit} className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="name">{t('form.name')}</Label>
              <Input
                id="name"
                type="text"
                placeholder={t('form.namePlaceholder')}
                value={registerForm.name}
                onChange={(e) => setRegisterForm({ ...registerForm, name: e.target.value })}
                required
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder={t('form.emailPlaceholder')}
                value={registerForm.email}
                onChange={(e) => setRegisterForm({ ...registerForm, email: e.target.value })}
                required
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="password">{t('form.password')}</Label>
              <Input
                id="password"
                type="password"
                placeholder="••••••••"
                value={registerForm.password}
                onChange={(e) => setRegisterForm({ ...registerForm, password: e.target.value })}
                showPasswordToggle={true}
                required
              />
            </div>
            <Button type="submit" className="w-full">{t('form.createAccount')}</Button>
            <Button variant="ghost" size="sm" className="text-xs text-muted-foreground hover:text-foreground" onClick={() => setDialogOpen('login')} type="button">
              {t('form.alreadyHaveAccount')}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={dialogOpen === 'recovery'} onOpenChange={(open) => !open && setDialogOpen(null)}>
        <DialogContent className="sm:max-w-md animate-fade-in">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold">{t('auth.recovery')}</DialogTitle>
            <DialogDescription>
              {t('auth.recoveryDesc')}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleRecoverySubmit} className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder={t('form.emailPlaceholder')}
                value={recoveryEmail}
                onChange={(e) => setRecoveryEmail(e.target.value)}
                required
              />
            </div>
            <Button type="submit" className="w-full">{t('form.sendLink')}</Button>
            <Button variant="ghost" size="sm" className="text-xs text-muted-foreground hover:text-foreground" onClick={() => setDialogOpen('login')} type="button">
              {t('form.backToLogin')}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </header>
  );
};

export default Header;
